import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  isInstallable,
  isStandaloneInstalled,
  promptInstall,
} from "../lib/pwa";

const DISMISS_KEY = "konattu_pwa_install_dismissed_at";
const HIDE_FOR_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Non-intrusive PWA install banner. Shows at the bottom on mobile when:
 *   • the browser fired `beforeinstallprompt` (i.e. site is installable)
 *   • the app is NOT already installed (standalone display-mode)
 *   • user hasn't dismissed it within the last 7 days
 *
 * Tapping "Install" triggers the native dialog via `promptInstall()`.
 */
export default function PWAInstallBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandaloneInstalled()) return;

    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < HIDE_FOR_MS) return;

    if (isInstallable()) setVisible(true);

    const onInstallable = () => setVisible(true);
    const onInstalled = () => setVisible(false);
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const onInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted" || outcome === "dismissed") {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
    setVisible(false);
  };

  const onDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed inset-x-3 bottom-20 z-[60] sm:left-auto sm:right-4 sm:bottom-4 sm:w-[360px] rounded-xl border border-red-100 bg-white shadow-xl"
    >
      <div className="flex items-start gap-3 p-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-600 text-white">
          <Download size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">Install Konattu HRMS</p>
          <p className="mt-0.5 text-xs text-gray-600">
            Get the app-like experience — full screen, faster loads, offline-ready.
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              className="h-8 bg-red-600 text-white hover:bg-red-700"
              onClick={onInstall}
              data-testid="pwa-install-confirm"
            >
              Install
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-gray-600"
              onClick={onDismiss}
              data-testid="pwa-install-dismiss"
            >
              Not now
            </Button>
          </div>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onDismiss}
          className="text-gray-400 hover:text-gray-600"
          data-testid="pwa-install-close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
